'use client'

import { play } from 'cuelume'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  ChartCandlestickIcon,
  PanelLeftClose,
  PanelLeftOpen,
  SlidersHorizontalIcon,
  TableIcon,
  TablePropertiesIcon,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'

import { Card } from '@/components/ui/card'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { AddableTabGroup, type Tab } from '@/components/widget/addable-tab-group'
import { FullscreenOverlay } from '@/components/widget/fullscreen-overlay'
import { LinkingActionHeader, WidgetActionsMenu } from '@/components/widget/linking-action-header'
import { WidgetMenuDropdownItem } from '@/components/widget/widget-menu-dropdown-item'
import { WidgetMenuRow } from '@/components/widget/widget-menu-row'
import { ChartMarketStatsRow } from '@/features/chart/components/chart-market-stats-row'
import { ChartSettingsContent } from '@/features/chart/components/chart-settings'
import OptionColumnsTab from '@/features/chart/components/dropdown/option-columns-tab'
import OptionTable from '@/features/chart/components/option-table'
import ChartWidget from '@/features/chart/components/widgets/chart-widget'
import { useChartCompare } from '@/features/chart/hooks/use-chart-compare'
import { useCrossExchangeOptions } from '@/features/chart/hooks/use-cross-exchange-options'
import { useResolvedChartEntry } from '@/features/chart/hooks/use-resolved-chart-entry'
import { buildOptionChainOverride } from '@/features/chart/lib/option-chain-override'
import { resolveOptionChainLandingVisibility } from '@/features/chart/lib/option-chain-view-mode'
import { parseOrderbookHeatmapSize } from '@/features/chart/lib/orderbook-heatmap'
import {
  buildOptionSymbolMap,
  deriveOptionTickerFromInstrument,
  filterAndGroupInstruments,
  getExpiryTimestamp,
  getNearesetExpiry,
  isExpiryStillAvailable,
  resolveOptionSymbol,
} from '@/features/chart/lib/utils/option-util'
import AccountFilterDropdown from '@/features/dashboard/components/dropdown/account-filter-dropdown'
import { WatchListPicker } from '@/features/dashboard/components/molecules/watch-list-picker'
import type { WatchListTabValue } from '@/features/dashboard/hook/use-watchlist-tab'
import { useWatchlistTab } from '@/features/dashboard/hook/use-watchlist-tab'
import { useWidgetConfigSync } from '@/features/dashboard/hooks/use-widget-config-sync'
import { LinkingPageContext } from '@/features/dashboard/lib/linking-page-context'
import { useInstrumentMap } from '@/features/order/order-form/hooks/use-instrument-map'
import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import { useCardAccountContext, useSetCardAccountId } from '@/hooks/use-card-account'
import { useLinkingGroupState } from '@/hooks/use-linking-group-state'
import { useOrderFormDispatch } from '@/hooks/use-order-form-dispatch'
import { toExchangeEnum } from '@/lib/proto/util'
import { cn } from '@/lib/utils'
import { accountsForVenue } from '@/lib/utils/account-select'
import { formatSymbol } from '@/lib/utils/format-util'
import { useAccountPrimaryExchange } from '@/service/account-exchanges.hook'
import { useUserAccounts } from '@/service/account.hook'
import { useInstrumentList } from '@/service/instrument.hook'
import {
  linkedChartAccountGroupKey,
  linkedChartAccountPickAtomFamily,
  linkedFocusedOptionContractAtomFamily,
} from '@/state/chart-account'
import { type ChartCompareMode, PERCEPT_COMPARE_MODES } from '@/state/chart-compare'
import { fullscreenGroupIdAtom } from '@/state/layout'
import { LinkingTickerInfo, LinkingTickerType } from '@/state/linking'
import { chartEngineAtom } from '@/state/storage'
import { mobileColumnSettingsCardIdAtom } from '@/state/widget-actions'
import {
  drawingPanelVisibleAtomFamily,
  optionChainViewVisibleAtomFamily,
} from '@/state/widget-instances'
import type { PerceptChartSettings } from '@/types/dashboard'
import { type Instrument, InstrumentKind } from '@/types/generated/common/types'

// Stable fallback for a chart with no Percept options set yet (keeps the menu's props steady).
const EMPTY_PERCEPT_CHART_SETTINGS: PerceptChartSettings = {}

export default function TabGroupedChart({ cardId: propCardId }: { cardId?: string } = {}) {
  const t = useTranslations('trade')
  const fallbackId = useId()
  const cardId = propCardId ?? fallbackId
  const { tabs, handleTabChange, removeTab, reorderTabs, updateTabTimeframe, selectedTimeframe } =
    useWatchlistTab(cardId)
  const {
    watchList,
    setSelectedTicker,
    selectedTicker,
    linkingColor,
    setSelectedOptionTicker,
    selectedOptionTicker,
    selectedOptionExpiry,
    setSelectedOptionExpiry,
    focusedOptionStrike,
    optionChainOverride,
    setOptionChainOverride,
    chartOption,
    setChartOption,
  } = useLinkingGroupState(cardId)
  const { setSelectedOptions } = useOrderFormDispatch(cardId)
  const mobileColumnSettingsCardId = useAtomValue(mobileColumnSettingsCardIdAtom)
  const setMobileColumnSettingsCardId = useSetAtom(mobileColumnSettingsCardIdAtom)
  const fullscreenGroupId = useAtomValue(fullscreenGroupIdAtom)
  const setFullscreenGroupId = useSetAtom(fullscreenGroupIdAtom)
  const { tradeVenue: cardTradeVenue, accountId } = useCardAccountContext(cardId)
  const setCardAccountId = useSetCardAccountId(cardId)
  // A row clicked in a linked Positions table brings this chart onto that row's account. Only a
  // new pick moves it: the value already there on mount is an old click, not a request.
  const linkingPageId = useContext(LinkingPageContext)
  const linkedAccountPick = useAtomValue(
    linkedChartAccountPickAtomFamily(linkedChartAccountGroupKey(linkingPageId, linkingColor))
  )
  const seenAccountPickRef = useRef(linkedAccountPick)
  useEffect(() => {
    if (linkedAccountPick === seenAccountPickRef.current) return
    seenAccountPickRef.current = linkedAccountPick
    if (linkedAccountPick && linkingColor !== 'unlinked')
      setCardAccountId(linkedAccountPick.accountId)
  }, [linkedAccountPick, linkingColor, setCardAccountId])
  const { data: accountsData } = useUserAccounts()
  const venueAccountIds = useMemo(
    () =>
      accountsForVenue(accountsData?.accounts ?? [], cardTradeVenue).map(
        (account) => account.accountId
      ),
    [accountsData?.accounts, cardTradeVenue]
  )
  const handleChartAccountChange = useCallback(
    (selectedIds: string[] | null) => {
      const nextAccountId = selectedIds?.[0]
      if (nextAccountId) setCardAccountId(nextAccountId)
    },
    [setCardAccountId]
  )
  const primaryExchange = useAccountPrimaryExchange(cardTradeVenue)
  const selectedTabExchange = useMemo(() => {
    const selectedItem = watchList.list[watchList.selectedIndex]
    return selectedItem ? toExchangeEnum(selectedItem.exchange) : primaryExchange
  }, [primaryExchange, watchList.list, watchList.selectedIndex])
  const { data: instrumentListData } = useInstrumentList(selectedTabExchange)
  const instruments = useMemo(() => instrumentListData?.instruments ?? [], [instrumentListData])

  // `optionSymbolMap`/`optionGroups` are derived further down, from
  // `optionChainInstruments` rather than from `instruments` — they need the active
  // tab's instrument resolved first to know whether its chain is cross-exchange.
  const instrumentMap = useInstrumentMap(selectedTabExchange)

  const [chartKey, setChartKey] = useState(0)
  const [pickerTarget, setPickerTarget] = useState<'add' | number | null>(null)
  // Letters typed on the chart before the picker takes focus; seeds its search box.
  const [pickerQuery, setPickerQuery] = useState('')
  const handleAddChartTabRequest = useCallback(() => {
    setPickerQuery('')
    setPickerTarget('add')
  }, [])
  // The chart-tab picker doubles as the compare picker: with a mode set in its
  // side panel, a pick is overlaid on this chart instead of opening a tab.
  const { addAsCompare, canCompare } = useChartCompare(cardId)
  const isPerceptEngine = useAtomValue(chartEngineAtom) === 'percept'
  const pickerCompareOptions = useMemo(
    () => ({
      onCompare: (instrument: Instrument, mode: ChartCompareMode) =>
        addAsCompare([{ exchange: instrument.exchange, symbol: instrument.symbol }], mode),
      // The side panel offers the mounted engine's own compare modes.
      modes: isPerceptEngine ? PERCEPT_COMPARE_MODES : undefined,
    }),
    [addAsCompare, isPerceptEngine]
  )
  const wasOfflineRef = useRef(false)
  const [isDrawingPanelVisible, setIsDrawingPanelVisible] = useAtom(
    drawingPanelVisibleAtomFamily(cardId)
  )
  const [isOptionChainVisible, setIsOptionChainVisible] = useAtom(
    optionChainViewVisibleAtomFamily(cardId)
  )
  const drawingToggleFnRef = useRef<(() => boolean) | null>(null)
  const { syncConfig } = useWidgetConfigSync()
  const handleRegisterDrawingToggle = useCallback((fn: () => boolean) => {
    drawingToggleFnRef.current = fn
  }, [])
  const handleFullscreenToggle = useCallback(() => {
    setFullscreenGroupId((currentGroupId) => (currentGroupId === cardId ? null : cardId))
  }, [cardId, setFullscreenGroupId])
  const showPositionLine = chartOption?.showPositionLine ?? true
  const showLiquidationLine = chartOption?.showLiquidationLine ?? showPositionLine
  const showBuySellTriangles = chartOption?.showBuySellTriangles ?? true
  const showBuySellButtons = chartOption?.showBuySellButtons ?? true
  const showOpenOrders = chartOption?.showOpenOrders ?? true
  const showIndex = chartOption?.showIndex ?? true
  const showTopBar = chartOption?.showTopBar ?? true
  const showHighLow = chartOption?.showHighLow ?? false
  const handleChartSettingChange = useCallback(
    (
      key:
        | 'showPositionLine'
        | 'showLiquidationLine'
        | 'showBuySellTriangles'
        | 'showBuySellButtons'
        | 'showOpenOrders'
        | 'showIndex'
        | 'showTopBar'
        | 'showHighLow',
      value: boolean
    ) => {
      setChartOption((prev) => ({ ...prev, [key]: value }))
      syncConfig(cardId, { [key]: String(value) })
    },
    [cardId, setChartOption, syncConfig]
  )
  const heatmapMinSize = parseOrderbookHeatmapSize(chartOption?.orderbookHeatmapMinSize)
  const heatmapMaxSize = parseOrderbookHeatmapSize(chartOption?.orderbookHeatmapMaxSize)
  const heatmapSizeFilter = useMemo(
    () => ({ min: heatmapMinSize, max: heatmapMaxSize }),
    [heatmapMaxSize, heatmapMinSize]
  )
  // Shared with both engines' heatmap: TV's overlay reads chartOption directly and copies it into
  // its study inputs; Percept's indicator settings follow via usePerceptHeatmapSync.
  const handleHeatmapSizeFilterChange = useCallback(
    ({ min, max }: { min: number; max: number }) => {
      setChartOption((prev) => ({
        ...prev,
        orderbookHeatmapMinSize: min,
        orderbookHeatmapMaxSize: max,
      }))
      syncConfig(cardId, {
        orderbookHeatmapMinSize: String(min),
        orderbookHeatmapMaxSize: String(max),
      })
    },
    [cardId, setChartOption, syncConfig]
  )
  const perceptChartSettings = chartOption?.perceptChartSettings
  const handlePerceptChartSettingsChange = useCallback(
    (patch: PerceptChartSettings) => {
      const next = { ...perceptChartSettings, ...patch }
      setChartOption((prev) => ({ ...prev, perceptChartSettings: next }))
      syncConfig(cardId, { perceptChartSettings: JSON.stringify(next) })
    },
    [cardId, perceptChartSettings, setChartOption, syncConfig]
  )

  // Persist drawing panel visibility to server config (skip initial render)
  const isMountedRef = useRef(false)
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true
      return
    }
    syncConfig(cardId, { drawingPanelVisible: String(isDrawingPanelVisible) })
  }, [isDrawingPanelVisible, cardId, syncConfig])

  // Cleanup per-widget atoms and chart content when this widget is unmounted
  const setChartOptionRef = useRef(setChartOption)
  setChartOptionRef.current = setChartOption
  const optionChainOverrideRef = useRef(optionChainOverride)
  optionChainOverrideRef.current = optionChainOverride
  const setOptionChainOverrideRef = useRef(setOptionChainOverride)
  setOptionChainOverrideRef.current = setOptionChainOverride

  useEffect(() => {
    return () => {
      drawingPanelVisibleAtomFamily.remove(cardId)
      optionChainViewVisibleAtomFamily.remove(cardId)
      setChartOptionRef.current((prev) => ({
        ...prev,
        content: undefined,
        perceptContent: undefined,
      }))
      // If this chart owned the option override, release it so linked widgets
      // fall back to selectedTicker/watchList instead of pointing at a symbol
      // whose owning chart no longer exists.
      if (optionChainOverrideRef.current?.sourceCardId === cardId) {
        setOptionChainOverrideRef.current(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── Chart-local active selection ─────────────────────────────────────────
  // watchList.selectedIndex may point at any item type, including options — the
  // chart renders whatever instrument is currently selected, unchanged.
  const { chartEntries, chartSelectedEntry } = useResolvedChartEntry({ watchList })

  // Chart tab bar mirrors every entry in the shared watchlist (chartEntries), including
  // options. Index mapping back to watchList.list is needed for remove/edit operations.
  const chartTabs = useMemo<Tab<WatchListTabValue>[]>(
    () =>
      chartEntries
        .map(({ item, originalIndex }) => {
          const existingTab = tabs[originalIndex]
          if (existingTab) return existingTab

          return {
            key: item.id,
            value: item.id,
            stableId: `synthetic-${item.exchange}-${item.symbol}`,
            symbol: item.symbol,
            exchange: toExchangeEnum(item.exchange),
            children: (
              <div className="flex min-w-0 items-center gap-2">
                <p className="truncate text-sm font-semibold tracking-tight">
                  {formatSymbol(item.symbol)}
                </p>
              </div>
            ),
          }
        })
        .filter((tab): tab is Tab<WatchListTabValue> => Boolean(tab)),
    [chartEntries, tabs]
  )

  const getOriginalIndexForChartTabIndex = useCallback(
    (chartTabIndex: number) => chartEntries[chartTabIndex]?.originalIndex ?? -1,
    [chartEntries]
  )

  const chartActiveIndex = useMemo(() => {
    if (!chartSelectedEntry) return -1
    return chartEntries.findIndex(
      ({ originalIndex }) => originalIndex === chartSelectedEntry.originalIndex
    )
  }, [chartEntries, chartSelectedEntry])
  const selectedSymbol = chartSelectedEntry?.item ?? null

  // Typing on the chart swaps the active tab's instrument, like TradingView's symbol search.
  // Keys that land before the picker has focus (a fast typist) keep appending.
  const handleTypeToSearch = useCallback(
    (key: string) => {
      const target = chartSelectedEntry ? chartSelectedEntry.originalIndex : 'add'
      setPickerQuery((query) => (pickerTarget === null ? key : query + key))
      setPickerTarget(target)
    },
    [chartSelectedEntry, pickerTarget]
  )

  const handleRemoveChartTab = useCallback(
    (chartTabIndex: number) => {
      const originalIndex = getOriginalIndexForChartTabIndex(chartTabIndex)
      if (originalIndex < 0) return
      removeTab(originalIndex)
    },
    [getOriginalIndexForChartTabIndex, removeTab]
  )

  // ─── Option ticker derivation ──────────────────────────────────────────────
  // Derived synchronously from the actual instrument list (not a ref), so a chart
  // that mounts before the instrument fetch resolves still picks up the ticker as
  // soon as `instruments` updates, instead of silently going stale.
  const activeInstrument = useMemo(
    () => instruments.find((item) => item.symbol === selectedSymbol?.symbol),
    [instruments, selectedSymbol?.symbol]
  )
  const activeOptionTicker = useMemo(
    () => (activeInstrument ? deriveOptionTickerFromInstrument(activeInstrument) : null),
    [activeInstrument]
  )

  const localOptionSymbolMap = useMemo(() => buildOptionSymbolMap(instruments), [instruments])
  const localOptionGroups = useMemo(() => {
    const optionInstruments = instruments.filter(
      (inst) => inst.kind === InstrumentKind.INSTRUMENT_KIND_OPTION
    )
    return filterAndGroupInstruments(optionInstruments)
  }, [instruments])
  const hasLocalOptionChain =
    activeOptionTicker !== null && localOptionGroups.has(activeOptionTicker)

  // ─── Cross-exchange option chain ──────────────────────────────────────────
  // `instruments` above is one exchange's list, so a tab on an underlying whose
  // options list elsewhere (today: an XNAS-listed ETF with its chain on OPRA) finds
  // no options in it and loses the chart/options toggle entirely. Walking
  // `Instrument.underlying` backwards over the full catalog recovers that chain, so
  // the toggle behaves the same as it does for a venue that lists both itself.
  const { instruments: crossExchangeOptions, exchange: crossExchangeOptionExchange } =
    useCrossExchangeOptions(activeInstrument, !hasLocalOptionChain)

  // The pool the option chain is read from: the tab's own exchange when it lists the
  // options (every crypto venue), the underlying's cross-exchange chain otherwise.
  // Never both — `optionGroups` keys by OSI root, so a same-root chain from two venues
  // would collapse into one group whose rows span exchanges.
  const optionChainInstruments =
    crossExchangeOptions.length > 0 ? crossExchangeOptions : instruments
  const optionChainExchange = crossExchangeOptionExchange ?? selectedTabExchange

  const optionSymbolMap = useMemo(
    () =>
      crossExchangeOptions.length > 0
        ? buildOptionSymbolMap(crossExchangeOptions)
        : localOptionSymbolMap,
    [crossExchangeOptions, localOptionSymbolMap]
  )
  const optionGroups = useMemo(() => {
    if (crossExchangeOptions.length === 0) return localOptionGroups
    const optionInstruments = optionChainInstruments.filter(
      (inst) => inst.kind === InstrumentKind.INSTRUMENT_KIND_OPTION
    )
    return filterAndGroupInstruments(optionInstruments)
  }, [crossExchangeOptions.length, localOptionGroups, optionChainInstruments])

  const previousSymbolRef = useRef<string | undefined>(undefined)
  const previousTabIdRef = useRef<string | undefined>(undefined)
  // Gated on the selected symbol/tab not having just changed this render (rather
  // than on selectedSymbol.type === 'options') — the tab switch updates selectedSymbol
  // synchronously in this render, but isOptionChainVisible only gets corrected
  // by the effect below on the NEXT render. Without this, picking a different
  // row right after viewing an option chain flashes the chain for one frame
  // before the effect flips it off. Using selection-change instead of type keeps
  // the switcher working for non-option instruments (e.g. ETH-PERPETUAL) that
  // have an associated option chain family.
  const selectionJustChanged =
    (previousSymbolRef.current !== undefined &&
      selectedSymbol?.symbol !== previousSymbolRef.current) ||
    (previousTabIdRef.current !== undefined && selectedSymbol?.id !== previousTabIdRef.current)
  // The very first `selectedSymbol` this widget observes is not necessarily the
  // FINAL one: watchList hydrates from a local snapshot before the server-side
  // widget-config restore (use-layout-sync.ts) can correct it, so right after a
  // page load/reload this can briefly still be an old/other tab's symbol. An
  // instant option-chain landing here used to trust that first value the same
  // way an explicit navigation is trusted — showing the options grid immediately
  // if it happened to be an option — even though a moment later the restore
  // would replace it with a plain perp (e.g. BTC-PERPETUAL), flashing options
  // first. Treat "first symbol we've seen" the same as "just changed" instead:
  // always defer through the correction effect below, so mount behaves exactly
  // like every subsequent tab switch (settles one render later, never lands
  // instantly on an option that might not be the real, restored selection).
  const isFirstKnownSymbol = previousSymbolRef.current === undefined && selectedSymbol !== null
  const isOptionMode: boolean =
    isOptionChainVisible &&
    !selectionJustChanged &&
    !isFirstKnownSymbol &&
    activeOptionTicker !== null &&
    optionGroups.has(activeOptionTicker)
  const previousWasOptionModeRef = useRef(false)

  // Broadcast so the option table (in this widget) and any other option-aware
  // consumers stay in sync with the currently selected instrument's family.
  const lastBroadcastedTickerRef = useRef<string | null>(null)
  useEffect(() => {
    if (!activeOptionTicker) return
    if (activeOptionTicker !== lastBroadcastedTickerRef.current) {
      lastBroadcastedTickerRef.current = activeOptionTicker
      if (activeOptionTicker !== selectedOptionTicker) {
        setSelectedOptionTicker(activeOptionTicker)
      }
    }
  }, [activeOptionTicker, selectedOptionTicker, setSelectedOptionTicker])

  // Default the mode whenever the concrete instrument being viewed changes (tab
  // replace, new tab opened + selected, tab removal selecting an adjacent tab,
  // external linking-group selection), plus a one-time correction on the initial
  // mount (see below). Ignores instrument-list refetch churn (compares the
  // primitive symbol string, not object identity).
  //
  // A non-option symbol always lands on Chart even if it has a fallback option
  // family; otherwise opening ETH-PERPETUAL after viewing ETH options would
  // incorrectly reopen the chain.
  //
  // An option tab defaults to the chain grid. Only a same-family contract opened
  // while the previous view was actually the option chain defaults to Chart, since
  // that action's intent is "show this contract's chart." Merely coming from a
  // same-family perp/chart must still open the chain.
  const previousOptionTickerRef = useRef<string | null>(null)
  useEffect(() => {
    const prev = previousSymbolRef.current
    const current = selectedSymbol?.symbol
    const previousTabId = previousTabIdRef.current
    const currentTabId = selectedSymbol?.id
    const previousOptionTicker = previousOptionTickerRef.current
    const symbolBecameAvailable = prev === undefined && current !== undefined
    const symbolChanged = prev !== undefined && prev !== current
    const tabChanged = previousTabId !== undefined && previousTabId !== currentTabId
    // The symbol can render before its instrument list. Re-evaluate once the
    // fallback/option family becomes known so the correct landing mode is not
    // missed merely because the query resolved one render later.
    const optionTickerResolved =
      prev === current && previousOptionTicker !== activeOptionTicker && activeOptionTicker !== null

    if (symbolBecameAvailable || symbolChanged || tabChanged || optionTickerResolved) {
      setIsOptionChainVisible(
        resolveOptionChainLandingVisibility({
          isOptionInstrument: selectedSymbol?.type === 'options',
          isOpenedFromOptionChain:
            previousWasOptionModeRef.current &&
            previousOptionTicker !== null &&
            previousOptionTicker === activeOptionTicker,
        })
      )
    }
    previousSymbolRef.current = current
    previousTabIdRef.current = currentTabId
    previousOptionTickerRef.current = activeOptionTicker
  }, [
    selectedSymbol?.id,
    selectedSymbol?.symbol,
    selectedSymbol?.type,
    activeOptionTicker,
    setIsOptionChainVisible,
  ])

  useEffect(() => {
    previousWasOptionModeRef.current = isOptionMode
  }, [isOptionMode])

  const handleOptionChainVisibilityChange = useCallback(
    (visible: boolean) => {
      play('toggle')
      setIsOptionChainVisible(visible)
    },
    [setIsOptionChainVisible]
  )

  // External triggers (position/order/vol-metrics tables — see e.g.
  // positions-tab-contents.tsx, data-widget.tsx) set focusedOptionStrike to jump
  // straight into the option chain for a specific contract. Always honor that by
  // showing the chain, overriding the opened-from-chain heuristic above — that one is
  // tuned for a different interaction (double-clicking/picking a row from
  // *within* the currently open grid), and would otherwise keep Chart mode when
  // an external trigger lands on a different expiry within the same ticker
  // family. Runs after the effect above so it wins when both fire in the same
  // commit. Depends on the whole object (not just `.ticker`) since its `_id`
  // is what forces a re-trigger on repeated clicks for the same ticker/expiry.
  //
  // Also requires the CURRENTLY selected tab to actually be an option —
  // focusedOptionStrike is written only on an option navigation and never
  // cleared afterward (every "open in chart" handler sets it solely inside
  // its `tickerType === 'options'` branch), so it can still equal
  // `selectedOptionTicker` after the user has since navigated to a plain
  // perp/spot/future in the same underlying family (e.g. viewed a BTC option,
  // then opened BTC-PERPETUAL from a History row) — that must land on Chart,
  // not silently reopen the option chain for an instrument that isn't one.
  useEffect(() => {
    if (!focusedOptionStrike) return
    if (selectedSymbol?.type !== 'options') return
    if (focusedOptionStrike.ticker !== selectedOptionTicker) return
    setIsOptionChainVisible(true)
  }, [focusedOptionStrike, selectedOptionTicker, selectedSymbol?.type, setIsOptionChainVisible])

  // ─── optionChainOverride ownership ─────────────────────────────────────────
  // Only the chart actually showing the option table for the matching family may
  // set the override — otherwise every price-chart widget sharing this linking
  // color would race to claim ownership on the same selectedTicker change.
  useEffect(() => {
    if (!isOptionChainVisible) return
    const nextOverride = buildOptionChainOverride({
      activeOptionTicker,
      cardId,
      exchange: optionChainExchange,
      instruments: optionChainInstruments,
      optionSymbolMap,
      selectedTicker,
    })
    if (nextOverride) setOptionChainOverride(nextOverride)
  }, [
    isOptionChainVisible,
    selectedTicker,
    activeOptionTicker,
    optionSymbolMap,
    optionChainExchange,
    optionChainInstruments,
    cardId,
    setOptionChainOverride,
  ])

  // Separate clear effect: the set effect above early-returns whenever option
  // mode is off, so it can never itself observe "option mode just closed" and
  // fire a clear. This effect owns clearing, gated on actually owning the override.
  useEffect(() => {
    const ownsOverride = optionChainOverride?.sourceCardId === cardId
    if (!ownsOverride) return
    const stillValid = isOptionChainVisible && selectedTicker?.type === 'options'
    if (stillValid) return
    setOptionChainOverride(null)
  }, [isOptionChainVisible, selectedTicker, optionChainOverride, cardId, setOptionChainOverride])

  // ─── Option expiry auto-select ─────────────────────────────────────────────
  // Auto-select nearest expiry only when the ticker family actually changes (or
  // the current expiry has expired/disappeared) — must not clobber an expiry the
  // user explicitly picked in the option table itself.
  const selectedOptionExpiryRef = useRef(selectedOptionExpiry)
  selectedOptionExpiryRef.current = selectedOptionExpiry
  const prevAutoExpiryTickerRef = useRef<string | null>(null)
  useEffect(() => {
    if (!selectedOptionTicker || instruments.length === 0) {
      return
    }

    const group = optionGroups.get(selectedOptionTicker)
    if (!group) {
      return
    }

    const tickerChanged = prevAutoExpiryTickerRef.current !== selectedOptionTicker
    prevAutoExpiryTickerRef.current = selectedOptionTicker

    // Exception: if the ticker change came from a position-table click,
    // positions-tab-contents already set the matching expiry directly via
    // setSelectedOptionExpiry — don't override it.
    if (focusedOptionStrike?.ticker === selectedOptionTicker) {
      return
    }

    const currentExpiry = selectedOptionExpiryRef.current
    const currentExpiryStillValid =
      !tickerChanged &&
      !!currentExpiry &&
      group.expires.has(currentExpiry) &&
      isExpiryStillAvailable(currentExpiry, group.expiryTsMs.get(currentExpiry))

    if (currentExpiryStillValid) {
      return
    }

    const availableExpiries = [...group.expires.keys()]
      .filter((expiry) => isExpiryStillAvailable(expiry, group.expiryTsMs.get(expiry)))
      .sort((a, b) => {
        const aTs = group.expiryTsMs.get(a) ?? getExpiryTimestamp(a)
        const bTs = group.expiryTsMs.get(b) ?? getExpiryTimestamp(b)
        return aTs - bTs
      })

    const nearest = getNearesetExpiry(availableExpiries)
    if (nearest && nearest !== currentExpiry) {
      setSelectedOptionExpiry(nearest)
    }
  }, [
    instruments.length,
    optionGroups,
    selectedOptionTicker,
    focusedOptionStrike,
    setSelectedOptionExpiry,
  ])

  // ─── Option-chain tab label ───────────────────────────────────────────────
  // The contract the option chain is focused on. The active tab names it in full while
  // the chain is showing, and falls back to the chain's general name ("BTC Option",
  // "BTC_USDC Option" — the watchlist wizard's naming) when nothing is focused. Landing on
  // an option tab focuses that contract, a row click focuses its row, Esc clears it.
  // Tracked here rather than read off selectedTicker alone: an unlinked chart never
  // broadcasts its tab into selectedTicker, so an option tab opened from the watchlist
  // would otherwise never read as focused.
  const [focusedChainSymbol, setFocusedChainSymbol] = useState<string | null>(null)
  useEffect(() => {
    setFocusedChainSymbol(selectedSymbol?.type === 'options' ? selectedSymbol.symbol : null)
  }, [selectedSymbol?.id, selectedSymbol?.symbol, selectedSymbol?.type])
  useEffect(() => {
    if (selectedTicker?.type === 'options') setFocusedChainSymbol(selectedTicker.symbol)
  }, [selectedTicker?.symbol, selectedTicker?.type])

  const optionChainTabLabel = useMemo(() => {
    if (!isOptionMode || !activeOptionTicker) return null
    const focused =
      focusedChainSymbol &&
      resolveOptionSymbol(focusedChainSymbol, optionSymbolMap)?.ticker === activeOptionTicker
        ? focusedChainSymbol
        : null
    return focused
      ? { symbol: focused, label: formatSymbol(focused) }
      : { symbol: null, label: t('optionChainTabLabel', { ticker: activeOptionTicker }) }
  }, [activeOptionTicker, focusedChainSymbol, isOptionMode, optionSymbolMap, t])

  // Shared with the link group: its Order Form seeds the focused contract (and only while one
  // is focused — the chain's general view seeds nothing).
  const focusedContractAtom = linkedFocusedOptionContractAtomFamily(
    linkedChartAccountGroupKey(linkingPageId, linkingColor)
  )
  const setFocusedContract = useSetAtom(focusedContractAtom)
  const focusedContractSymbol = optionChainTabLabel?.symbol ?? null
  const focusedContractExchange = selectedSymbol?.exchange
  useEffect(() => {
    if (linkingColor === 'unlinked') return
    const exchange = focusedContractExchange ? toExchangeEnum(focusedContractExchange) : undefined
    setFocusedContract((prev) => {
      if (focusedContractSymbol && exchange !== undefined) {
        return prev?.cardId === cardId &&
          prev.symbol === focusedContractSymbol &&
          prev.exchange === exchange
          ? prev
          : { cardId, exchange, symbol: focusedContractSymbol }
      }
      return prev?.cardId === cardId ? null : prev
    })
  }, [cardId, focusedContractExchange, focusedContractSymbol, linkingColor, setFocusedContract])
  useEffect(
    () => () => setFocusedContract((prev) => (prev?.cardId === cardId ? null : prev)),
    [cardId, setFocusedContract]
  )

  const labeledChartTabs = useMemo(() => {
    if (!optionChainTabLabel || chartActiveIndex < 0 || !selectedSymbol) return chartTabs
    return chartTabs.map((tab, index) =>
      index === chartActiveIndex
        ? {
            ...tab,
            // Keyed to what the label names, so the hover card and its live ticker match;
            // the general name has no contract of its own, so it keeps the tab's symbol.
            symbol: optionChainTabLabel.symbol ?? selectedSymbol.symbol,
            children: optionChainTabLabel.label,
          }
        : tab
    )
  }, [chartActiveIndex, chartTabs, optionChainTabLabel, selectedSymbol])

  // ESC key: clear option override and deselect → linked widgets revert to the
  // non-option selection, and the tab falls back to the chain's general name.
  const hasFocusedChainContract = isOptionMode && focusedChainSymbol !== null
  useEffect(() => {
    if (selectedTicker?.type !== 'options' && !hasFocusedChainContract) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFocusedChainSymbol(null)
        if (selectedTicker?.type !== 'options') return
        setSelectedTicker(null)
        setSelectedOptions([])
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [hasFocusedChainContract, selectedTicker?.type, setSelectedTicker, setSelectedOptions])

  const handleChartTabChange = useCallback(
    (value: WatchListTabValue) => {
      setChartOption((prev) => ({ ...prev, scale: 'Normal', autoScale: true }))
      syncConfig(cardId, { scale: 'Normal', autoScale: 'true' })
      handleTabChange(value)
    },
    [cardId, handleTabChange, setChartOption, syncConfig]
  )

  // ─── Sync selectedTicker to linking group ─────────────────────────────────
  // Only fires when the chart's own active tab changes — not when an external widget
  // (e.g. option-row click) updates selectedTicker. Reading selectedTicker via a ref
  // avoids including it in deps and prevents an unchanged chart from overriding external
  // selections.
  const selectedTickerRef = useRef(selectedTicker)
  selectedTickerRef.current = selectedTicker

  useEffect(() => {
    if (!chartSelectedEntry || linkingColor === 'unlinked') {
      return
    }
    const item = chartSelectedEntry.item
    const tickerType = item.type as LinkingTickerType
    const baseCurrency = resolveBaseCurrency(instrumentMap, item.symbol)
    // Options tabs are now legitimate chart tabs — follow them the same way as any
    // other type, including the contract's expiry (parsed via resolveOptionSymbol)
    // so linked widgets (orderbook, etc.) subscribe to the exact contract.
    const expiry =
      tickerType === 'options'
        ? (resolveOptionSymbol(item.symbol, optionSymbolMap)?.expiry ?? undefined)
        : undefined
    const tickerInfo: LinkingTickerInfo = {
      symbol: item.symbol,
      type: tickerType,
      baseCurrency,
      expiry,
    }
    const cur = selectedTickerRef.current
    if (
      cur?.symbol === tickerInfo.symbol &&
      cur.type === tickerInfo.type &&
      cur.baseCurrency === tickerInfo.baseCurrency &&
      cur.expiry === tickerInfo.expiry
    ) {
      return
    }
    setSelectedTicker(tickerInfo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartSelectedEntry, linkingColor, setSelectedTicker])

  // ─── Track online/offline status ──────────────────────────────────────────
  useEffect(() => {
    const handleOnline = () => {
      if (wasOfflineRef.current) {
        setChartKey((prev) => prev + 1)
        wasOfflineRef.current = false
      }
    }

    const handleOffline = () => {
      wasOfflineRef.current = true
    }

    if (typeof window !== 'undefined') {
      wasOfflineRef.current = !navigator.onLine
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const hasOptionChain = activeOptionTicker !== null && optionGroups.has(activeOptionTicker)

  return (
    <>
      <FullscreenOverlay groupId={cardId}>
        <Card className="flex h-full min-w-0 flex-col overflow-auto">
          <div className="widget-drag-handle cursor-move overflow-visible border-b border-white/10">
            <AddableTabGroup
              containerClassName="border-b-0"
              list={labeledChartTabs}
              activeIndex={chartActiveIndex}
              topLeftCorner={
                <LinkingActionHeader cardId={cardId} className="gap-0 pr-0" disableActionsMenu />
              }
              topRightCorner={
                <div
                  className="ml-auto flex items-center gap-1"
                  data-tutorial={hasOptionChain ? undefined : 'views'}
                >
                  <AccountFilterDropdown
                    cardId={cardId}
                    accountIds={venueAccountIds}
                    selectionMode="single"
                    selection={{
                      selectedIds: accountId ? [accountId] : [],
                      onChange: handleChartAccountChange,
                    }}
                  />
                  {hasOptionChain && (
                    // SegmentedControl doesn't forward props; this flex wrapper is layout-neutral.
                    <div className="mr-1 flex" data-tutorial="views">
                      <SegmentedControl
                        size="xs"
                        iconOnly
                        value={isOptionMode ? 'options' : 'chart'}
                        onChange={(mode) => handleOptionChainVisibilityChange(mode === 'options')}
                        options={[
                          {
                            value: 'chart',
                            label: t('chartModeChart'),
                            icon: <ChartCandlestickIcon className="size-3" />,
                          },
                          {
                            value: 'options',
                            label: `${t('chartModeOptions')} · ${activeOptionTicker}`,
                            icon: <TableIcon className="size-3" />,
                          },
                        ]}
                      />
                    </div>
                  )}
                  {/* Chart Settings (position/liquidation/order lines) live in this menu. */}
                  <div className="flex" data-tutorial="overlays">
                    <WidgetActionsMenu
                      cardId={cardId}
                      additionalItems={(closeMenu) => (
                        <>
                          {isOptionMode ? (
                            <WidgetMenuDropdownItem
                              icon={<TablePropertiesIcon className="size-3.5 rotate-180" />}
                              label="Columns"
                            >
                              <OptionColumnsTab />
                            </WidgetMenuDropdownItem>
                          ) : (
                            <WidgetMenuDropdownItem
                              icon={<SlidersHorizontalIcon className="size-3.5" />}
                              label="Chart Settings"
                            >
                              <ChartSettingsContent
                                baseCurrency={resolveBaseCurrency(
                                  instrumentMap,
                                  selectedSymbol?.symbol ?? ''
                                )}
                                positionSizeUnit={chartOption?.positionSizeUnit ?? 'base'}
                                setPositionSizeUnit={(positionSizeUnit) => {
                                  setChartOption((prev) => ({ ...prev, positionSizeUnit }))
                                  syncConfig(cardId, { positionSizeUnit })
                                }}
                                showPositionLine={showPositionLine}
                                setShowPositionLine={(value) =>
                                  handleChartSettingChange('showPositionLine', value)
                                }
                                showLiquidationLine={showLiquidationLine}
                                setShowLiquidationLine={(value) =>
                                  handleChartSettingChange('showLiquidationLine', value)
                                }
                                showBuySellTriangles={showBuySellTriangles}
                                setShowBuySellTriangles={(value) =>
                                  handleChartSettingChange('showBuySellTriangles', value)
                                }
                                showBuySellButtons={showBuySellButtons}
                                setShowBuySellButtons={(value) =>
                                  handleChartSettingChange('showBuySellButtons', value)
                                }
                                showOpenOrders={showOpenOrders}
                                setShowOpenOrders={(value) =>
                                  handleChartSettingChange('showOpenOrders', value)
                                }
                                showIndex={showIndex}
                                setShowIndex={(value) =>
                                  handleChartSettingChange('showIndex', value)
                                }
                                showTopBar={showTopBar}
                                setShowTopBar={(value) =>
                                  handleChartSettingChange('showTopBar', value)
                                }
                                showHighLow={showHighLow}
                                setShowHighLow={(value) =>
                                  handleChartSettingChange('showHighLow', value)
                                }
                                perceptChartSettings={
                                  perceptChartSettings ?? EMPTY_PERCEPT_CHART_SETTINGS
                                }
                                setPerceptChartSettings={handlePerceptChartSettingsChange}
                                heatmapSizeFilter={heatmapSizeFilter}
                                setHeatmapSizeFilter={handleHeatmapSizeFilterChange}
                              />
                            </WidgetMenuDropdownItem>
                          )}
                          {!isOptionMode && (
                            <WidgetMenuRow
                              icon={
                                isDrawingPanelVisible ? (
                                  <PanelLeftClose className="text-foreground/60 size-3.5" />
                                ) : (
                                  <PanelLeftOpen className="text-foreground/60 size-3.5" />
                                )
                              }
                              label={isDrawingPanelVisible ? t('hideTools') : t('showTools')}
                              title={isDrawingPanelVisible ? t('hideTools') : t('showTools')}
                              onClick={() => {
                                drawingToggleFnRef.current?.()
                                closeMenu()
                              }}
                            />
                          )}
                        </>
                      )}
                    />
                  </div>
                </div>
              }
              onTabChange={handleChartTabChange}
              onReorder={reorderTabs}
              removeTab={handleRemoveChartTab}
              onAddRequested={handleAddChartTabRequest}
              onEditRequested={(idx) => {
                setPickerQuery('')
                setPickerTarget(getOriginalIndexForChartTabIndex(idx))
              }}
            />
          </div>
          <WatchListPicker
            cardId={cardId}
            open={pickerTarget !== null}
            onOpenChange={(next) => {
              if (!next) setPickerTarget(null)
            }}
            replaceIndex={typeof pickerTarget === 'number' ? pickerTarget : undefined}
            initialQuery={pickerQuery}
            chartCompare={canCompare ? pickerCompareOptions : undefined}
          />
          {selectedSymbol ? (
            <div className="relative flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
              {/* Option chain layer — mounted only in option mode (its own WS
                  subscription cost), overlaid above the chart layer below. */}
              {isOptionMode && (
                <div className="absolute inset-0 flex min-h-0 w-full min-w-0 flex-col overflow-hidden">
                  <OptionTable
                    instruments={optionChainInstruments}
                    cardId={cardId}
                    focusTicker={activeOptionTicker}
                    exchange={optionChainExchange}
                  />
                </div>
              )}
              {/* Chart layer — stays mounted for the whole lifetime of this widget
                  (never unmounted just because isOptionMode toggles), so switching
                  to/from the option chain doesn't tear down and recreate the
                  TradingView widget. Hidden purely via CSS while option mode is on;
                  the wrapper keeps its layout size so TV's `autosize` doesn't
                  collapse to 0px. ChartMarketStatsRow keeps its original mount
                  policy (only when visible) since it holds its own market-data
                  subscription. */}
              <div
                className={cn(
                  'flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden',
                  isOptionMode && 'pointer-events-none invisible absolute inset-0'
                )}
              >
                {showTopBar && !isOptionMode && (
                  <ChartMarketStatsRow
                    symbol={selectedSymbol.symbol}
                    exchange={toExchangeEnum(selectedSymbol.exchange)}
                    pricePrecision={
                      activeInstrument?.tickSize ??
                      selectedSymbol.pricePrecision ??
                      selectedSymbol.tickSize
                    }
                    instrument={activeInstrument}
                    accountId={accountId}
                  />
                )}
                {/* Indicators and drawing tools live inside the TradingView iframe. */}
                <div
                  className="flex min-h-0 w-full min-w-0 flex-1 overflow-hidden"
                  data-tutorial="indicators"
                >
                  <ChartWidget
                    key={chartKey}
                    cardId={cardId}
                    symbol={selectedSymbol.symbol}
                    exchange={selectedSymbol.exchange}
                    timeframe={selectedTimeframe ?? 60 * 60}
                    onTimeframeChange={updateTabTimeframe}
                    onRegisterDrawingToggle={handleRegisterDrawingToggle}
                    onDrawingPanelVisibilityChange={setIsDrawingPanelVisible}
                    initialDrawingPanelVisible={isDrawingPanelVisible}
                    showIndex={showIndex}
                    onAddChartTabRequest={handleAddChartTabRequest}
                    onTypeToSearch={handleTypeToSearch}
                    isFullscreen={fullscreenGroupId === cardId}
                    onFullscreenToggle={handleFullscreenToggle}
                    realtimeEnabled={!isOptionMode}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-foreground/50 flex flex-1 items-center justify-center text-sm">
              {t('noSymbolSelected')}. {t('addSymbolFromWatchlist')}
            </div>
          )}
        </Card>
      </FullscreenOverlay>
      <Sheet
        open={mobileColumnSettingsCardId === cardId}
        onOpenChange={(open) => {
          if (!open) setMobileColumnSettingsCardId(null)
        }}
      >
        <SheetContent
          side="bottom"
          hideClose
          showHandle
          className="max-h-[50dvh] overflow-y-auto p-0 pt-3"
        >
          <SheetHeader className="px-4 pb-3">
            <SheetTitle className="text-center text-sm font-medium">
              {t('columnSettings')}
            </SheetTitle>
          </SheetHeader>
          <div className="overflow-y-auto px-4 pb-6">
            <OptionColumnsTab className="max-h-none w-full p-0" />
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
